import { findPresetByPresetIdInArray } from 'components/presetManager/helpers/TreeHelperFunctions'
import { TreeItem } from 'react-sortable-tree'

export const parsePresetNodes = async (
    tree: TreeItem[],
    presetId: number,
    refetchPresetNodesData: (variables: any) => Promise<any>,
    existingPresetNodesData?: any
): Promise<TreeItem[]> => {
    try {
        // Collect all preset node IDs that need to be fetched
        const presetNodeIds: number[] = []
        const collectPresetNodeIds = (node: TreeItem) => {
            if (node && node.hasOwnProperty('expanded')) {
                const { has_nodes_of_presets: hasNodesOfPresets } = node
                const index = findPresetByPresetIdInArray(hasNodesOfPresets, presetId)

                if (index > -1) {
                    const presetNodeId = hasNodesOfPresets?.[index]?.preset_node_id
                    const normalizedPresetNodeId = parseInt(presetNodeId)
                    if (presetNodeId && !presetNodeIds.includes(normalizedPresetNodeId)) {
                        presetNodeIds.push(normalizedPresetNodeId)
                    }
                }
            }

            if (node.children && Array.isArray(node.children)) {
                node.children.forEach(collectPresetNodeIds)
            }
        }

        // Recursively collect preset node IDs
        tree.forEach(collectPresetNodeIds)

        // Fetch all preset nodes in batch
        let presetNodesMap: Map<number, any> = new Map()

        // First, try to use existing data if available
        if (existingPresetNodesData?.configuratorPresetNodes?.data) {
            existingPresetNodesData.configuratorPresetNodes.data.forEach((presetNode) => {
                presetNodesMap.set(parseInt(presetNode.id), presetNode)
            })
        }

        // If we don't have data for all needed nodes, refetch
        const missingNodeIds = presetNodeIds.filter((id) => !presetNodesMap.has(id))

        if (missingNodeIds.length > 0) {
            try {
                const { data } = await refetchPresetNodesData({
                    filter: { preset_id: presetId }
                })

                if (data?.configuratorPresetNodes?.data) {
                    data.configuratorPresetNodes.data.forEach((presetNode) => {
                        presetNodesMap.set(parseInt(presetNode.id), presetNode)
                    })
                }
            } catch (error) {
                console.warn('Failed to fetch preset nodes in batch:', error)
            }
        }

        // Synchronously apply preset data to all nodes
        const applyPresetData = (node: TreeItem): TreeItem => {
            // Always assign presetId for consistency
            node.presetId = presetId

            const { has_nodes_of_presets: hasNodesOfPresets } = node
            const index = findPresetByPresetIdInArray(hasNodesOfPresets, presetId)

            if (index > -1) {
                const presetNodeId = hasNodesOfPresets?.[index]?.preset_node_id
                node.presetNodeId = presetNodeId

                // Ensure consistent type handling for preset node lookup
                const normalizedPresetNodeId = parseInt(presetNodeId)
                const presetNode = presetNodesMap.get(normalizedPresetNodeId)
                if (presetNode) {
                    node.presetNode = presetNode
                    node.hasPresetLoading = false
                } else {
                    node.hasPresetLoading = true
                }
            }

            // Process children
            if (node.children && Array.isArray(node.children)) {
                node.children = node.children.map(applyPresetData)
            }

            return node
        }

        // Apply preset data to all nodes in tree
        const resultTree = tree.map(applyPresetData)
        return resultTree
    } catch (error) {
        console.error('Error parsing preset nodes:', error)
        return tree
    }
}
